import { useState, useEffect, useCallback, useRef } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { useNavigate } from 'react-router-dom'
import {
  Search,
  LayoutDashboard,
  Clock,
  Star,
  TrendingUp,
  Calendar,
  Heart,
  Sparkles,
  Code2,
  Settings,
  CreditCard,
  ShieldAlert,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useUser } from '@/store'

const ALL_COMMANDS = [
  { to: '/dashboard',     icon: LayoutDashboard, label: 'Dashboard',      sanskrit: 'मुख्य',     group: 'Navigation' },
  { to: '/daily-timings', icon: Clock,            label: 'Daily Timings',  sanskrit: 'चौघड़िया',   group: 'Navigation' },
  { to: '/birth-chart',   icon: Star,             label: 'Birth Chart',    sanskrit: 'जन्म',      group: 'Navigation' },
  { to: '/dasha',         icon: TrendingUp,       label: 'Dasha Timeline', sanskrit: 'दशा',       group: 'Navigation' },
  { to: '/muhurta',       icon: Calendar,         label: 'Muhurta',        sanskrit: 'मुहूर्त',    group: 'Guidance' },
  { to: '/event-muhurta', icon: Search,           label: 'Event Finder',   sanskrit: 'शुभ',       group: 'Guidance' },
  { to: '/compatibility', icon: Heart,            label: 'Kundali Match',  sanskrit: 'कुंडली',    group: 'Guidance' },
  { to: '/insights',      icon: Sparkles,         label: 'Influences',     sanskrit: 'ग्रह',      group: 'Guidance' },
  { to: '/remedies',      icon: Star,             label: 'Remedies',       sanskrit: 'उपाय',      group: 'Guidance' },
  { to: '/settings',      icon: Settings,         label: 'Settings',       sanskrit: null,        group: 'Account' },
  { to: '/billing',       icon: CreditCard,       label: 'Billing',        sanskrit: null,        group: 'Account' },
  { to: '/developer',     icon: Code2,            label: 'API Dashboard',  sanskrit: 'API',       group: 'Developer' },
]

const ADMIN_COMMAND = {
  to: '/admin/users', icon: ShieldAlert, label: 'Admin Panel', sanskrit: null, group: 'Account',
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CommandPalette({ open, onOpenChange }: Props) {
  const navigate = useNavigate()
  const user = useUser()
  const isAdmin = user?.role?.toUpperCase() === 'ADMIN'

  const commands = isAdmin ? [...ALL_COMMANDS, ADMIN_COMMAND] : ALL_COMMANDS

  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const filtered = query.trim()
    ? commands.filter(c => c.label.toLowerCase().includes(query.toLowerCase()))
    : commands

  // Group items for rendering
  const groups = filtered.reduce<Record<string, typeof filtered>>((acc, item) => {
    if (!acc[item.group]) acc[item.group] = []
    acc[item.group].push(item)
    return acc
  }, {})

  useEffect(() => {
    setActiveIndex(0)
  }, [query])

  useEffect(() => {
    if (!open) {
      setQuery('')
      setActiveIndex(0)
    } else {
      // Re-focus input after animation frame
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [open])

  // Scroll active item into view
  useEffect(() => {
    const activeEl = listRef.current?.querySelector('[data-active="true"]') as HTMLElement | null
    activeEl?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  const execute = useCallback(
    (to: string) => {
      navigate(to)
      onOpenChange(false)
    },
    [navigate, onOpenChange],
  )

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex(i => Math.min(i + 1, filtered.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      const item = filtered[activeIndex]
      if (item) execute(item.to)
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-black/70 backdrop-blur-sm',
            'data-[state=open]:animate-fade-in-overlay',
            'data-[state=closed]:animate-fade-out-overlay',
          )}
        />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-[18vh] z-50 w-full max-w-lg -translate-x-1/2',
            'rounded-2xl overflow-hidden',
            'bg-cosmic-nebula/95 ring-1 ring-white/10',
            'shadow-[0_32px_80px_rgba(0,0,0,0.8),0_0_0_1px_rgba(167,139,250,0.08)]',
            'backdrop-blur-2xl',
            'data-[state=open]:animate-scale-in',
            'data-[state=closed]:animate-scale-out',
            'focus:outline-none',
          )}
          onKeyDown={handleKeyDown}
          aria-label="Command palette"
        >
          <Dialog.Title className="sr-only">Command Palette — navigate to a page</Dialog.Title>

          {/* Search input row */}
          <div className="flex items-center gap-3 px-4 py-3.5 border-b border-white/5">
            <Search className="w-4 h-4 text-star-muted flex-shrink-0" aria-hidden="true" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Go to a page…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              className="flex-1 bg-transparent text-sm text-star-bright placeholder:text-star-dim outline-none"
              autoComplete="off"
              spellCheck={false}
            />
            {!query && (
              <kbd className="hidden sm:flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-cosmic-dusk border border-white/10 text-star-dim text-2xs font-mono">
                esc
              </kbd>
            )}
          </div>

          {/* Results list */}
          <div ref={listRef} className="max-h-[360px] overflow-y-auto">
            {filtered.length === 0 && (
              <div className="px-4 py-10 text-center">
                <p className="text-sm text-star-muted">No results for &ldquo;{query}&rdquo;</p>
              </div>
            )}

            {Object.entries(groups).map(([group, items]) => (
              <div key={group} className="py-1">
                <p className="px-4 pt-2 pb-1 text-2xs font-mono uppercase tracking-widest text-star-dim select-none">
                  {group}
                </p>
                {items.map(item => {
                  const globalIndex = filtered.indexOf(item)
                  const isActive = activeIndex === globalIndex
                  return (
                    <button
                      key={item.to}
                      data-active={isActive}
                      onClick={() => execute(item.to)}
                      onMouseEnter={() => setActiveIndex(globalIndex)}
                      className={cn(
                        'w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-75',
                        isActive
                          ? 'bg-cosmic-aurora text-star-white'
                          : 'text-star hover:bg-cosmic-dusk/60',
                      )}
                    >
                      <span
                        className={cn(
                          'w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0',
                          isActive ? 'bg-gold/20' : 'bg-cosmic-dusk',
                        )}
                      >
                        <item.icon className={cn('w-3.5 h-3.5', isActive ? 'text-gold' : 'text-gold/60')} />
                      </span>
                      <span className="flex-1 text-left truncate">{item.label}</span>
                      {item.sanskrit && (
                        <span className={cn('text-2xs font-display flex-shrink-0', isActive ? 'text-gold/60' : 'text-star-dim')}>
                          {item.sanskrit}
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="px-4 py-2.5 border-t border-white/5 flex items-center gap-4 text-star-dim text-2xs font-mono select-none">
            <span className="flex items-center gap-1"><kbd className="text-star-muted">↑↓</kbd> navigate</span>
            <span className="flex items-center gap-1"><kbd className="text-star-muted">↵</kbd> open</span>
            <span className="flex items-center gap-1"><kbd className="text-star-muted">esc</kbd> close</span>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
